import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Upload, 
  X, 
  Star,
  Download, 
  Copy, 
  Loader2,
  CheckCircle,
  Grid3X3,
  LayoutGrid,
  Columns,
  ArrowLeft,
  Image as ImageIcon,
  Package,
  Smartphone,
  RectangleVertical,
  Square,
  RefreshCw,
  Plus,
  Trash2,
  Check,
  Type,
  Settings,
  Cloud,
  Search,
  MessageCircle,
  Building2
} from "lucide-react";
import { toast } from "sonner";
import PropertyOverlays, { DEFAULT_OVERLAYS } from './PropertyOverlays';
import { AppHeader } from './AppHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_STARRED = 4;

const OUTPUT_SIZES = [
  { key: "instagram_story", label: "Story", dimensions: "1080×1920", ratio: "9:16", icon: Smartphone },
  { key: "instagram_portrait", label: "Standard", dimensions: "1080×1350", ratio: "4:5", icon: RectangleVertical },
  { key: "instagram_square", label: "Square", dimensions: "1080×1080", ratio: "1:1", icon: Square },
  { key: "original", label: "Original", dimensions: "No Crop", ratio: "As Is", icon: ImageIcon },
];

const COLLAGE_TEMPLATES = [
  { key: "2_split", label: "2-Image Split", icon: Columns, minImages: 2, description: "Side by side" },
  { key: "3_grid", label: "3-Image Grid", icon: Grid3X3, minImages: 3, description: "Three in a row" },
  { key: "4_grid", label: "4-Image Grid", icon: LayoutGrid, minImages: 4, description: "2×2 grid" },
];

const LOGO_POSITIONS = [
  { value: "north_west", label: "Top Left" },
  { value: "north", label: "Top Centre" },
  { value: "north_east", label: "Top Right" },
  { value: "west", label: "Middle Left" },
  { value: "center", label: "Centre" },
  { value: "east", label: "Middle Right" },
  { value: "south_west", label: "Bottom Left" },
  { value: "south", label: "Bottom Centre" },
  { value: "south_east", label: "Bottom Right" },
];

function SocialImageBuilder({ onBack }) {
  // Upload state
  const [uploadedImages, setUploadedImages] = useState([]);
  const [starredImageIds, setStarredImageIds] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);

  // Options state
  const [selectedSizes, setSelectedSizes] = useState(["instagram_story", "instagram_portrait"]);
  const [generateCollages, setGenerateCollages] = useState(false);
  const [selectedCollageTemplates, setSelectedCollageTemplates] = useState(["2_split", "4_grid"]);

  // Logo/Branding state
  const [logoEnabled, setLogoEnabled] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [logoPosition, setLogoPosition] = useState("north");
  const [logoScale, setLogoScale] = useState(60);
  const [logoPadding, setLogoPadding] = useState(10);
  const [logoOpacity, setLogoOpacity] = useState(100);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [showLogoSettings, setShowLogoSettings] = useState(false);

  // Overlays (text only now)
  const [showOverlays, setShowOverlays] = useState(false);
  const [propertyOverlays, setPropertyOverlays] = useState(DEFAULT_OVERLAYS);
  const [groupSamePosition, setGroupSamePosition] = useState(true);
  const [sharedPosition, setSharedPosition] = useState('south_west');
  const [groupBackground, setGroupBackground] = useState({ enabled: true, color: 'rgba(0,0,0,0.6)', rounded: true });
  
  // Text overlay per-image control
  const [textOnFirstOnly, setTextOnFirstOnly] = useState(true); // Default: text only on first image
  const [imagesWithText, setImagesWithText] = useState([]); // Custom selection of images that get text
  
  // Preview aspect ratio state
  const [previewAspect, setPreviewAspect] = useState('9:16');
  
  // Preview image selection (for clicking to edit)
  const [previewImageIndex, setPreviewImageIndex] = useState(0);

  // Library state - persists generated images
  const [imageLibrary, setImageLibrary] = useState([]);
  const [selectedLibraryIds, setSelectedLibraryIds] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // R2 Import state
  const [imageSource, setImageSource] = useState('upload'); // 'upload' or 'r2'
  const [r2Buckets, setR2Buckets] = useState([]);
  const [r2SelectedBucket, setR2SelectedBucket] = useState('');
  const [r2JobId, setR2JobId] = useState('');
  const [r2Loading, setR2Loading] = useState(false);
  const [r2Images, setR2Images] = useState([]); // Raw R2 images before import
  const [r2SelectedKeys, setR2SelectedKeys] = useState([]); // Selected R2 images for import
  const [r2Importing, setR2Importing] = useState(false);

  // Per-image logo control
  const [imagesWithoutLogo, setImagesWithoutLogo] = useState([]);

  // WhatsApp state
  const [agents, setAgents] = useState([]);
  const [selectedAgentPhone, setSelectedAgentPhone] = useState('');
  const [showWhatsApp, setShowWhatsApp] = useState(false);

  // WMS Property Address
  const [propertyAddress, setPropertyAddress] = useState(null);

  // Load company logo on mount
  useEffect(() => {
    loadCompanyLogo();
    loadR2Buckets();
    loadAgents();
  }, []);

  const loadCompanyLogo = async () => {
    try {
      const response = await axios.get(`${API}/company/logo`);
      if (response.data.success && response.data.logo) {
        setCompanyLogo(response.data.logo);
      }
    } catch (error) {
      console.error("Error loading company logo:", error);
    }
  };

  const loadR2Buckets = async () => {
    try {
      const response = await axios.get(`${API}/social-video/r2-buckets`);
      const configured = response.data.buckets.filter(b => b.configured);
      setR2Buckets(configured);
      if (configured.length > 0) {
        setR2SelectedBucket(response.data.default || configured[0].id);
      }
    } catch (error) {
      console.error("Error loading R2 buckets:", error);
    }
  };

  const loadAgents = async () => {
    try {
      const response = await axios.get(`${API}/admin/agents`);
      setAgents(response.data || []);
    } catch (error) {
      console.error("Error loading agents:", error);
    }
  };

  const handleLoadR2Images = async () => {
    if (!r2JobId.trim()) {
      toast.error("Please enter a Job ID");
      return;
    }
    setR2Loading(true);
    setR2Images([]);
    setR2SelectedKeys([]);
    setPropertyAddress(null);
    try {
      // Load R2 images and WMS order details in parallel
      const [r2Response, wmsResponse] = await Promise.allSettled([
        axios.post(`${API}/social/load-r2-images`, {
          job_id: r2JobId.trim(),
          bucket: r2SelectedBucket
        }),
        axios.get(`${API}/wms/order/${r2JobId.trim()}`)
      ]);

      if (r2Response.status === 'fulfilled' && r2Response.value.data.success) {
        setR2Images(r2Response.value.data.images);
        setR2SelectedKeys(r2Response.value.data.images.map(img => img.url));
        toast.success(`Found ${r2Response.value.data.total} images`);
      } else if (r2Response.status === 'rejected') {
        const detail = r2Response.reason?.response?.data?.detail || "Failed to load images from R2";
        toast.error(detail);
      }

      if (wmsResponse.status === 'fulfilled' && wmsResponse.value.data.full_address) {
        setPropertyAddress(wmsResponse.value.data);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to load images from R2";
      toast.error(detail);
    } finally {
      setR2Loading(false);
    }
  };

  const toggleR2Selection = (url) => {
    setR2SelectedKeys(prev =>
      prev.includes(url) ? prev.filter(u => u !== url) : [...prev, url]
    );
  };

  const handleImportR2 = async () => {
    if (r2SelectedKeys.length === 0) {
      toast.error("Please select at least one image");
      return;
    }
    if (uploadedImages.length + r2SelectedKeys.length > 20) {
      toast.error("Maximum 20 images allowed");
      return;
    }
    setR2Importing(true);
    try {
      const response = await axios.post(`${API}/social/upload-from-r2`, {
        image_urls: r2SelectedKeys,
        job_id: r2JobId.trim()
      });
      if (response.data.success) {
        setUploadedImages(prev => [...prev, ...response.data.uploaded]);
        toast.success(`Imported ${response.data.total_uploaded} images`);
        setR2Images([]);
        setR2SelectedKeys([]);
      }
      if (response.data.errors?.length > 0) {
        toast.error(`${response.data.total_failed} image(s) failed to import`);
      }
    } catch (error) {
      toast.error("Failed to import images from R2");
    } finally {
      setR2Importing(false);
    }
  };

  // Toggle per-image logo
  const toggleImageLogo = (publicId) => {
    setImagesWithoutLogo(prev =>
      prev.includes(publicId) ? prev.filter(id => id !== publicId) : [...prev, publicId]
    );
  };

  const imageHasLogo = (publicId) => {
    return logoEnabled && !imagesWithoutLogo.includes(publicId);
  };

  // WhatsApp share
  const handleWhatsAppShare = () => {
    const phone = selectedAgentPhone.replace(/[^0-9+]/g, '');
    if (!phone) {
      toast.error("Please select an agent or enter a phone number");
      return;
    }

    // Group images by format
    const formatGroups = {};
    imageLibrary.forEach(asset => {
      const label = asset.format_label || asset.category || 'Other';
      if (!formatGroups[label]) formatGroups[label] = [];
      formatGroups[label].push(asset.secure_url);
    });

    // Build message
    const addr = propertyAddress?.full_address || '';
    let message = addr
      ? `Property images for: ${addr}\nJob: ${r2JobId || 'N/A'}\n\n`
      : `Property images - Job ${r2JobId || 'N/A'}\n\n`;
    Object.entries(formatGroups).forEach(([format, urls]) => {
      message += `${format} (${urls.length}):\n`;
      urls.forEach(url => { message += `${url}\n`; });
      message += '\n';
    });

    const encodedMsg = encodeURIComponent(message.trim());
    const cleanPhone = phone.startsWith('+') ? phone.slice(1) : phone;
    window.open(`https://wa.me/${cleanPhone}?text=${encodedMsg}`, '_blank');
    toast.success("WhatsApp opened");
  };

  // Toggle text overlay for a specific image
  const toggleImageText = (imagePublicId) => {
    setTextOnFirstOnly(false); // Switch to custom mode
    setImagesWithText(prev => {
      if (prev.includes(imagePublicId)) {
        return prev.filter(id => id !== imagePublicId);
      }
      return [...prev, imagePublicId];
    });
  };
  
  // Check if an image has text enabled
  const imageHasText = (imagePublicId) => {
    if (textOnFirstOnly) {
      return uploadedImages[0]?.public_id === imagePublicId;
    }
    return imagesWithText.includes(imagePublicId);
  };

  // Handle text overlays toggle - enable all overlays when turned on
  const handleTextOverlaysToggle = (enabled) => {
    setShowOverlays(enabled);
    if (enabled) {
      // Enable all overlays when toggling on
      setPropertyOverlays(prev => {
        const updated = { ...prev };
        Object.keys(DEFAULT_OVERLAYS).forEach(key => {
          updated[key] = { 
            ...DEFAULT_OVERLAYS[key], 
            ...prev[key], 
            enabled: true 
          };
        });
        return updated;
      });
    }
  };

  const handleLogoUpload = async (fileOrEvent) => {
    const file = fileOrEvent?.target?.files?.[0] || fileOrEvent;
    if (!file) return;

    setIsUploadingLogo(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/company/logo`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data.success) {
        setCompanyLogo({
          public_id: response.data.public_id,
          url: response.data.url,
          secure_url: response.data.secure_url
        });
        toast.success("Logo uploaded");
      }
    } catch (error) {
      console.error("Logo upload error:", error);
      toast.error("Failed to upload logo");
    } finally {
      setIsUploadingLogo(false);
      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }
    }
  };

  // Handle file upload
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate total count
    if (uploadedImages.length + files.length > 20) {
      toast.error("Maximum 20 images allowed");
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      files.forEach(file => formData.append("files", file));

      const response = await axios.post(`${API}/social/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data.success) {
        setUploadedImages(prev => [...prev, ...response.data.uploaded]);
        toast.success(`${response.data.total_uploaded} image(s) uploaded`);
      }

      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => {
          toast.error(`Failed: ${err.filename}`);
        });
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload images");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Toggle star on an image
  const toggleStar = (imageId) => {
    setStarredImageIds(prev => {
      if (prev.includes(imageId)) {
        return prev.filter(id => id !== imageId);
      } else {
        if (prev.length >= MAX_STARRED) {
          toast.error(`Maximum ${MAX_STARRED} images can be starred for collages`);
          return prev;
        }
        return [...prev, imageId];
      }
    });
  };

  // Remove image
  const removeImage = async (imageId, publicId) => {
    setUploadedImages(prev => prev.filter(img => img.id !== imageId));
    setStarredImageIds(prev => prev.filter(id => id !== publicId));
    
    try {
      await axios.delete(`${API}/social/image/${encodeURIComponent(publicId)}`);
    } catch (error) {
      console.error("Error deleting image:", error);
    }
  };

  // Toggle size selection
  const toggleSize = (sizeKey) => {
    setSelectedSizes(prev => {
      if (prev.includes(sizeKey)) {
        if (prev.length === 1) {
          toast.error("At least one size must be selected");
          return prev;
        }
        return prev.filter(s => s !== sizeKey);
      }
      return [...prev, sizeKey];
    });
  };

  // Toggle collage template
  const toggleCollageTemplate = (templateKey) => {
    setSelectedCollageTemplates(prev =>
      prev.includes(templateKey)
        ? prev.filter(t => t !== templateKey)
        : [...prev, templateKey]
    );
  };

  // Generate all images - ADD to library
  const handleGenerate = async () => {
    if (uploadedImages.length === 0) {
      toast.error("Please upload at least one image");
      return;
    }

    if (selectedSizes.length === 0) {
      toast.error("Please select at least one output size");
      return;
    }

    if (generateCollages && starredImageIds.length < 2) {
      toast.error("Please star at least 2 images for collage generation");
      return;
    }

    setIsProcessing(true);

    try {
      // Build property overlays data for API
      // When groupSamePosition is true, use sharedPosition for all overlays
      const activePropertyOverlays = Object.entries(propertyOverlays)
        .filter(([_, config]) => config.enabled && config.value)
        .reduce((acc, [key, config]) => {
          acc[key] = {
            text: config.value,
            font_size: config.fontSize,
            position: groupSamePosition ? sharedPosition : config.position,
            font: config.font || 'Arial',
            color: config.color,
            order: config.order || 0
          };
          return acc;
        }, {});
      
      const hasPropertyOverlays = Object.keys(activePropertyOverlays).length > 0;

      // Send selected sizes directly to backend
      const response = await axios.post(`${API}/social/process`, {
        all_image_ids: uploadedImages.map(img => img.public_id),
        starred_image_ids: starredImageIds,
        selected_sizes: selectedSizes,
        generate_collages: generateCollages,
        collage_templates: selectedCollageTemplates,
        logo_enabled: logoEnabled && companyLogo !== null,
        logo_public_id: logoEnabled ? companyLogo?.public_id : null,
        logo_position: logoPosition,
        logo_scale: logoScale,
        logo_padding: logoPadding,
        logo_opacity: logoOpacity,
        property_overlays_enabled: hasPropertyOverlays,
        property_overlays: hasPropertyOverlays ? activePropertyOverlays : null,
        // Group settings for text overlays
        overlay_group_position: sharedPosition,
        overlay_group_background: groupBackground,
        // Per-image text control
        text_on_first_only: textOnFirstOnly,
        images_with_text: !textOnFirstOnly && imagesWithText.length > 0 ? imagesWithText : null,
        // Per-image logo control
        images_without_logo: imagesWithoutLogo.length > 0 ? imagesWithoutLogo : null
      });

      if (response.data.success) {
        // Collect all new assets and add to library
        const newAssets = [];
        
        // Individual crops
        if (response.data.individual_crops) {
          Object.entries(response.data.individual_crops).forEach(([formatKey, assets]) => {
            assets.forEach(asset => {
              newAssets.push({
                ...asset,
                type: 'crop',
                category: formatKey,
                addedAt: Date.now()
              });
            });
          });
        }
        
        // Collages
        if (response.data.collages) {
          Object.entries(response.data.collages).forEach(([formatKey, assets]) => {
            assets.forEach(asset => {
              newAssets.push({
                ...asset,
                type: 'collage',
                category: formatKey,
                addedAt: Date.now()
              });
            });
          });
        }
        
        // Add to library (preserve existing)
        setImageLibrary(prev => [...prev, ...newAssets]);
        toast.success(`Added ${response.data.total_count} images to library`);
        
        // Scroll to library
        setTimeout(() => {
          document.getElementById("image-library")?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    } catch (error) {
      console.error("Processing error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate images");
    } finally {
      setIsProcessing(false);
    }
  };

  // Start Over - clear library
  const handleStartOver = () => {
    setImageLibrary([]);
    setSelectedLibraryIds([]);
    toast.info("Library cleared");
  };

  // Toggle library item selection
  const toggleLibrarySelection = (assetId) => {
    setSelectedLibraryIds(prev => 
      prev.includes(assetId)
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };

  // Select all in library
  const selectAllLibrary = () => {
    if (selectedLibraryIds.length === imageLibrary.length) {
      setSelectedLibraryIds([]);
    } else {
      setSelectedLibraryIds(imageLibrary.map(a => a.id));
    }
  };

  // Remove selected from library
  const removeSelectedFromLibrary = () => {
    setImageLibrary(prev => prev.filter(a => !selectedLibraryIds.includes(a.id)));
    setSelectedLibraryIds([]);
    toast.info("Removed selected images");
  };

  // Copy URL
  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL copied");
    } catch (error) {
      toast.error("Failed to copy URL");
    }
  };

  // Download single image
  const downloadImage = async (asset) => {
    try {
      const response = await fetch(asset.secure_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = asset.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Download failed");
    }
  };

  // Detect iOS/iPad
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  // Save to camera roll (mobile)
  const saveToDevice = async (asset) => {
    try {
      if (isIOS()) {
        const newWindow = window.open(asset.secure_url, '_blank');
        if (newWindow) {
          toast.info("Long-press the image and tap 'Add to Photos'", { duration: 4000 });
        } else {
          const response = await fetch(asset.secure_url);
          const blob = await response.blob();
          const file = new File([blob], asset.filename, { type: blob.type });
          
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file] });
            toast.success("Use 'Save Image' to add to Photos");
          }
        }
        return;
      }
      
      const response = await fetch(asset.secure_url);
      const blob = await response.blob();
      const file = new File([blob], asset.filename, { type: blob.type });
      
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: asset.filename,
        });
        toast.success("Saved to device");
      } else {
        downloadImage(asset);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        downloadImage(asset);
      }
    }
  };

  // Download selected images
  const downloadSelectedImages = async () => {
    const selectedAssets = imageLibrary.filter(a => selectedLibraryIds.includes(a.id));
    if (selectedAssets.length === 0) {
      toast.error("No images selected");
      return;
    }
    
    toast.info(`Downloading ${selectedAssets.length} images...`);
    for (const asset of selectedAssets) {
      await downloadImage(asset);
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    toast.success("Downloads complete");
  };

  // Download all images in library
  const downloadAllImages = async () => {
    if (imageLibrary.length === 0) {
      toast.error("No images in library");
      return;
    }
    
    toast.info(`Downloading ${imageLibrary.length} images...`);
    for (const asset of imageLibrary) {
      await downloadImage(asset);
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    toast.success("All downloads complete");
  };

  // Get starred images in order
  const getStarredImages = () => {
    return starredImageIds
      .map(id => uploadedImages.find(img => img.public_id === id))
      .filter(Boolean);
  };

  // Group library by type
  const cropAssets = imageLibrary.filter(a => a.type === 'crop');
  const collageAssets = imageLibrary.filter(a => a.type === 'collage');

  return (
    <div className="social-builder">
      {/* Header */}
      <AppHeader onBack={onBack} />

      <div className="social-content">
        <div className="builder-layout">
          {/* Left Column: Settings (Scrollable) */}
          <div className="settings-column">
            <div className="social-form">
              {/* Source Toggle + Upload/R2 Import */}
              <div className="upload-formats-row">
                {/* Property Address Banner (from WMS) */}
                {propertyAddress && propertyAddress.full_address && (
                  <div className="property-address-banner" data-testid="property-address-banner">
                    <div className="property-address-icon">
                      <Building2 size={18} />
                    </div>
                    <div className="property-address-details">
                      <span className="property-address-text">{propertyAddress.full_address}</span>
                      <span className="property-address-meta">
                        {propertyAddress.bedrooms ? `${propertyAddress.bedrooms} bed` : ''}
                        {propertyAddress.bedrooms && propertyAddress.package_name ? ' · ' : ''}
                        {propertyAddress.package_name || ''}
                      </span>
                    </div>
                  </div>
                )}
                {/* Left: Source Section */}
                <div className="upload-column">
                  {/* Source Toggle */}
                  <div className="source-toggle" data-testid="source-toggle">
                    <button
                      className={`source-btn ${imageSource === 'upload' ? 'active' : ''}`}
                      onClick={() => setImageSource('upload')}
                      data-testid="source-upload-btn"
                    >
                      <Upload size={14} /> Upload
                    </button>
                    <button
                      className={`source-btn ${imageSource === 'r2' ? 'active' : ''}`}
                      onClick={() => setImageSource('r2')}
                      data-testid="source-r2-btn"
                    >
                      <Cloud size={14} /> Import from R2
                    </button>
                  </div>

                  {imageSource === 'upload' ? (
                    <>
                      <Label className="section-title">
                        <Upload className="section-icon" />
                        Upload Images
                      </Label>
                      <div 
                        className="upload-zone compact"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleFileSelect}
                          style={{ display: "none" }}
                          data-testid="image-upload-input"
                        />
                        {isUploading ? (
                          <div className="upload-loading">
                            <Loader2 className="animate-spin upload-icon" />
                            <span>Uploading...</span>
                          </div>
                        ) : (
                          <div className="upload-prompt">
                            <Upload className="upload-icon" />
                            <span className="upload-text">Click or drag images here</span>
                            <span className="upload-hint">JPG, PNG, WEBP</span>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <Label className="section-title">
                        <Cloud className="section-icon" />
                        Import from R2 Bucket
                      </Label>
                      <div className="r2-import-controls" data-testid="r2-import-controls">
                        {r2Buckets.length > 1 && (
                          <select
                            className="r2-bucket-select"
                            value={r2SelectedBucket}
                            onChange={(e) => setR2SelectedBucket(e.target.value)}
                            data-testid="r2-bucket-select"
                          >
                            {r2Buckets.map(b => (
                              <option key={b.id} value={b.id}>{b.label}</option>
                            ))}
                          </select>
                        )}
                        <div className="r2-job-row">
                          <input
                            type="text"
                            placeholder="Enter Job ID..."
                            value={r2JobId}
                            onChange={(e) => setR2JobId(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleLoadR2Images()}
                            className="r2-job-input"
                            data-testid="r2-job-id-input"
                          />
                          <button
                            className="r2-load-btn"
                            onClick={handleLoadR2Images}
                            disabled={r2Loading || !r2JobId.trim()}
                            data-testid="r2-load-btn"
                          >
                            {r2Loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                            {r2Loading ? 'Searching...' : 'Load'}
                          </button>
                        </div>
                      </div>

                      {/* R2 Image Grid */}
                      {r2Images.length > 0 && (
                        <div className="r2-results" data-testid="r2-results">
                          <div className="r2-results-header">
                            <span>{r2Images.length} images found</span>
                            <button
                              className="r2-select-all"
                              onClick={() => {
                                if (r2SelectedKeys.length === r2Images.length) {
                                  setR2SelectedKeys([]);
                                } else {
                                  setR2SelectedKeys(r2Images.map(img => img.url));
                                }
                              }}
                            >
                              {r2SelectedKeys.length === r2Images.length ? 'Deselect All' : 'Select All'}
                            </button>
                          </div>
                          <div className="r2-image-grid">
                            {r2Images.map((img) => {
                              const isSelected = r2SelectedKeys.includes(img.url);
                              return (
                                <div
                                  key={img.key}
                                  className={`r2-image-card ${isSelected ? 'selected' : 'deselected'}`}
                                  onClick={() => toggleR2Selection(img.url)}
                                  data-testid={`r2-image-${img.order}`}
                                >
                                  <img src={img.thumbnail_url || img.url} alt={img.filename} />
                                  <div className={`r2-check ${isSelected ? 'checked' : ''}`}>
                                    {isSelected ? <Check size={14} /> : <X size={14} />}
                                  </div>
                                  <span className="r2-filename">{img.filename}</span>
                                </div>
                              );
                            })}
                          </div>
                          <button
                            className="r2-import-btn"
                            onClick={handleImportR2}
                            disabled={r2Importing || r2SelectedKeys.length === 0}
                            data-testid="r2-import-btn"
                          >
                            {r2Importing ? (
                              <><Loader2 size={14} className="animate-spin" /> Importing...</>
                            ) : (
                              <><Plus size={14} /> Import {r2SelectedKeys.length} Selected</>
                            )}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Right: Output Formats 2x2 Grid */}
                <div className="formats-column">
                  <Label className="section-title">
                    <ImageIcon className="section-icon" />
                    Output Formats
                  </Label>
                  <div className="sizes-grid-2x2">
                    {OUTPUT_SIZES.map((size) => {
                      const Icon = size.icon;
                      const isSelected = selectedSizes.includes(size.key);
                      return (
                        <button
                          key={size.key}
                          type="button"
                          className={`size-button-compact ${isSelected ? 'selected' : ''}`}
                          onClick={() => toggleSize(size.key)}
                          data-testid={`size-${size.key}`}
                        >
                          <CheckCircle className={`check-icon ${isSelected ? 'visible' : ''}`} />
                          <Icon className="size-icon" />
                          <div className="size-text">
                            <span className="size-label">{size.label}</span>
                            <span className="size-ratio">{size.ratio}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Collage Toggle - Inline under formats */}
                  <div className="collage-inline-toggle">
                    <label className="collage-check">
                      <input 
                        type="checkbox"
                        checked={generateCollages}
                        onChange={(e) => setGenerateCollages(e.target.checked)}
                        data-testid="collage-toggle"
                      />
                      <LayoutGrid size={14} />
                      <span>Generate as collages</span>
                    </label>
                    {generateCollages && starredImageIds.length < 2 && (
                      <span className="collage-hint">Star 2+ images below</span>
                    )}
                    {generateCollages && starredImageIds.length >= 2 && (
                      <span className="collage-ready">{starredImageIds.length} images selected</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Uploaded Images Grid - Full Width Below */}
              {uploadedImages.length > 0 && (
                <div className="images-section">
                  <div className="images-header">
                    <span className="images-count">{uploadedImages.length} image{uploadedImages.length !== 1 ? 's' : ''} uploaded</span>
                    {generateCollages && (
                      <span className="star-hint">
                        <Star className="star-hint-icon" />
                        Star images for collages
                      </span>
                    )}
                  </div>
                  <div className="images-grid">
                    {uploadedImages.map((image, idx) => {
                      const isStarred = starredImageIds.includes(image.public_id);
                      const starIndex = starredImageIds.indexOf(image.public_id);
                      const hasText = imageHasText(image.public_id);
                      const isPreviewImage = idx === previewImageIndex;
                      return (
                        <div 
                          key={image.id} 
                          className={`image-card ${isStarred ? 'starred' : ''} ${hasText ? 'has-text' : ''} ${isPreviewImage ? 'preview-selected' : ''}`}
                          onClick={() => setPreviewImageIndex(idx)}
                        >
                          <img 
                            src={image.thumbnail_url || image.secure_url} 
                            alt={image.original_filename}
                            className="image-thumb"
                          />
                          {/* Text indicator */}
                          {hasText && (
                            <div className="text-indicator" title="Text overlay enabled">
                              <Type size={10} />
                            </div>
                          )}
                          <button 
                            className={`star-button ${isStarred ? 'active' : ''}`}
                            onClick={(e) => { e.stopPropagation(); toggleStar(image.public_id); }}
                            title={isStarred ? 'Remove from collage' : 'Add to collage'}
                          >
                            <Star className={`star-icon ${isStarred ? 'filled' : ''}`} />
                            {isStarred && <span className="star-number">{starIndex + 1}</span>}
                          </button>
                          <button 
                            className="remove-button"
                            onClick={(e) => { e.stopPropagation(); removeImage(image.id, image.public_id); }}
                            title="Remove image"
                          >
                            <X className="remove-icon" />
                          </button>
                          {/* Per-image logo toggle */}
                          {logoEnabled && companyLogo && (
                            <button
                              className={`logo-toggle-btn ${imageHasLogo(image.public_id) ? 'active' : 'off'}`}
                              onClick={(e) => { e.stopPropagation(); toggleImageLogo(image.public_id); }}
                              title={imageHasLogo(image.public_id) ? 'Logo ON - click to remove' : 'Logo OFF - click to add'}
                              data-testid={`logo-toggle-${image.id}`}
                            >
                              <ImageIcon size={10} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {/* Text placement hint */}
                  <div className="text-placement-hint">
                    <Type size={12} />
                    <span>
                      {textOnFirstOnly 
                        ? 'Text overlays on first image only' 
                        : `Text on ${imagesWithText.length || 0} selected images`}
                    </span>
                    <button 
                      className="text-mode-toggle"
                      onClick={() => {
                        if (textOnFirstOnly) {
                          setTextOnFirstOnly(false);
                          setImagesWithText([uploadedImages[0]?.public_id].filter(Boolean));
                        } else {
                          setTextOnFirstOnly(true);
                          setImagesWithText([]);
                        }
                      }}
                    >
                      {textOnFirstOnly ? 'Customize' : 'Reset to first only'}
                    </button>
                  </div>
                </div>
              )}

          {/* Logo Watermark Section */}
          <div className="logo-section">
            <div className="section-header-row">
              <div className="section-header-left">
                <ImageIcon size={16} className="section-icon" />
                <span>Logo Watermark</span>
              </div>
              <div className="section-header-right">
                <Switch
                  checked={logoEnabled}
                  onCheckedChange={setLogoEnabled}
                  data-testid="logo-enabled-toggle"
                />
                {logoEnabled && (
                  <button 
                    className={`settings-cog-btn ${showLogoSettings ? 'active' : ''}`}
                    onClick={() => setShowLogoSettings(!showLogoSettings)}
                    data-testid="logo-settings-btn"
                  >
                    <Settings size={16} />
                  </button>
                )}
              </div>
            </div>
            
            {showLogoSettings && logoEnabled && (
              <div className="logo-settings-compact">
                {/* All controls in one clean row */}
                <div className="logo-controls-row">
                  {/* Logo Upload/Preview */}
                  <div className="logo-upload-cell">
                    {companyLogo ? (
                      <div className="logo-thumb">
                        <img src={companyLogo.secure_url || companyLogo.url} alt="Logo" />
                        <button className="logo-remove-x" onClick={() => setCompanyLogo(null)}>×</button>
                      </div>
                    ) : (
                      <button 
                        className="logo-upload-small"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={isUploadingLogo}
                      >
                        {isUploadingLogo ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                      </button>
                    )}
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      style={{ display: 'none' }}
                    />
                  </div>

                  {/* Position */}
                  <div className="logo-control-item">
                    <label>Position</label>
                    <select value={logoPosition} onChange={(e) => setLogoPosition(e.target.value)}>
                      {LOGO_POSITIONS.map(pos => (
                        <option key={pos.value} value={pos.value}>{pos.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Size */}
                  <div className="logo-control-item">
                    <label>Size</label>
                    <div className="slider-compact">
                      <input type="range" min="10" max="100" value={logoScale} onChange={(e) => setLogoScale(Number(e.target.value))} />
                      <span>{logoScale}%</span>
                    </div>
                  </div>

                  {/* Padding */}
                  <div className="logo-control-item">
                    <label>Padding</label>
                    <div className="slider-compact">
                      <input type="range" min="0" max="60" value={logoPadding} onChange={(e) => setLogoPadding(Number(e.target.value))} />
                      <span>{logoPadding}px</span>
                    </div>
                  </div>

                  {/* Opacity */}
                  <div className="logo-control-item">
                    <label>Opacity</label>
                    <div className="slider-compact">
                      <input type="range" min="20" max="100" value={logoOpacity} onChange={(e) => setLogoOpacity(Number(e.target.value))} />
                      <span>{logoOpacity}%</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Text Overlays Section */}
          <div className="overlays-section">
            <div 
              className={`section-header-row ${showOverlays ? 'active' : ''}`}
              onClick={() => handleTextOverlaysToggle(!showOverlays)}
              role="button"
              tabIndex={0}
              data-testid="overlays-toggle"
              style={{ cursor: 'pointer' }}
            >
              <div className="section-header-left">
                <Type size={16} className="section-icon" />
                <span>Text Overlays</span>
              </div>
              <div className="section-header-right">
                <Switch
                  checked={showOverlays}
                  onCheckedChange={handleTextOverlaysToggle}
                  onClick={(e) => e.stopPropagation()}
                  data-testid="text-overlays-toggle"
                />
              </div>
            </div>
            
            {showOverlays && (
              <div className="overlays-settings-panel">
                <PropertyOverlays
                  overlays={propertyOverlays}
                  setOverlays={setPropertyOverlays}
                  groupSamePosition={groupSamePosition}
                  setGroupSamePosition={setGroupSamePosition}
                  sharedPosition={sharedPosition}
                  setSharedPosition={setSharedPosition}
                  groupBackground={groupBackground}
                  setGroupBackground={setGroupBackground}
                />
              </div>
            )}
          </div>

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            disabled={isProcessing || uploadedImages.length === 0}
            className="generate-button"
            data-testid="generate-btn"
          >
            {isProcessing ? (
              <>
                <Loader2 className="animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Plus className="btn-icon" />
                {imageLibrary.length > 0 ? 'Add More to Library' : 'Generate Social Pack'}
              </>
            )}
          </Button>
            </div>
          </div>

          {/* Right Column: Sticky Preview */}
          <div className="preview-column">
            <div className="sticky-preview">
              <div className="preview-header-label">
                <span>Live Preview</span>
                {uploadedImages.length > 1 && (
                  <span className="preview-nav-hint">
                    Image {previewImageIndex + 1} of {uploadedImages.length}
                  </span>
                )}
              </div>
              <div className={`sticky-preview-frame aspect-${previewAspect.replace(':', '-')}`}>
                {uploadedImages.length > 0 ? (
                  <>
                    <img 
                      src={uploadedImages[previewImageIndex]?.secure_url || uploadedImages[0].secure_url} 
                      alt="Preview" 
                      className="preview-bg-img" 
                    />
                    {/* Image navigation arrows */}
                    {uploadedImages.length > 1 && (
                      <>
                        <button 
                          className="preview-nav-btn prev"
                          onClick={() => setPreviewImageIndex(i => Math.max(0, i - 1))}
                          disabled={previewImageIndex === 0}
                        >
                          ‹
                        </button>
                        <button 
                          className="preview-nav-btn next"
                          onClick={() => setPreviewImageIndex(i => Math.min(uploadedImages.length - 1, i + 1))}
                          disabled={previewImageIndex === uploadedImages.length - 1}
                        >
                          ›
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <div className="preview-placeholder-text">
                    <ImageIcon size={32} />
                    <span>Upload an image to see preview</span>
                  </div>
                )}
                
                {/* Render text overlays - only if current preview image has text */}
                {uploadedImages.length > 0 && imageHasText(uploadedImages[previewImageIndex]?.public_id) && (() => {
                  // Group overlays by position for stacking
                  const activeOverlays = Object.entries(propertyOverlays)
                    .filter(([_, config]) => config.enabled && config.value);
                  
                  // Sort by order if available
                  activeOverlays.sort((a, b) => (a[1].order || 0) - (b[1].order || 0));
                  
                  // Get position style helper function
                  const getPositionStyle = (pos) => {
                    let style = { position: 'absolute' };
                    
                    // Horizontal positioning
                    if (pos.includes('west')) {
                      style.left = '5%';
                      style.textAlign = 'left';
                    } else if (pos.includes('east')) {
                      style.right = '5%';
                      style.textAlign = 'right';
                    } else {
                      // Center horizontally (north, south, center, mid_north, mid_south)
                      style.left = '50%';
                      style.transform = 'translateX(-50%)';
                      style.textAlign = 'center';
                    }
                    
                    // Vertical positioning
                    if (pos === 'north' || pos === 'north_west' || pos === 'north_east') {
                      style.top = '5%';
                    } else if (pos === 'south' || pos === 'south_west' || pos === 'south_east') {
                      style.bottom = '5%';
                    } else if (pos === 'mid_north' || pos === 'mid_north_west' || pos === 'mid_north_east') {
                      style.top = '25%';
                    } else if (pos === 'mid_south' || pos === 'mid_south_west' || pos === 'mid_south_east') {
                      style.bottom = '25%';
                    } else {
                      // center, west, east - vertical center
                      style.top = '50%';
                      style.transform = (style.transform || '') + ' translateY(-50%)';
                    }
                    
                    return style;
                  };
                  
                  const getFontSize = (size) => {
                    const sizes = { small: 12, medium: 16, large: 20, xlarge: 26 };
                    return sizes[size] || 16;
                  };
                  
                  // Always render grouped (groupSamePosition is always true now)
                  if (activeOverlays.length > 0) {
                    const textAlign = sharedPosition.includes('east') ? 'right' : sharedPosition.includes('west') ? 'left' : 'center';
                    
                    return (
                      <div
                        key="grouped-overlays"
                        className="preview-text-group"
                        style={{
                          ...getPositionStyle(sharedPosition),
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '2px',
                          backgroundColor: groupBackground.enabled ? groupBackground.color : 'transparent',
                          borderRadius: groupBackground.enabled && groupBackground.rounded ? '8px' : '0',
                          padding: groupBackground.enabled ? '0.5rem 0.75rem' : '0',
                          textAlign: textAlign,
                          zIndex: 10,
                        }}
                      >
                        {activeOverlays.map(([key, config]) => {
                          const showShadow = config.textShadow !== false;
                          return (
                            <div
                              key={key}
                              style={{
                                fontSize: `${getFontSize(config.fontSize)}px`,
                                fontFamily: config.font || 'Arial',
                                color: config.color,
                                textShadow: showShadow 
                                  ? (config.color === '#FFFFFF' || config.color === '#f5f5f5' 
                                    ? '1px 1px 3px rgba(0,0,0,0.8)' 
                                    : '1px 1px 3px rgba(255,255,255,0.5)')
                                  : 'none',
                                fontWeight: 600,
                                lineHeight: 1.3,
                              }}
                            >
                              {config.value}
                            </div>
                          );
                        })}
                      </div>
                    );
                  }
                  
                  return null;
                })()}
                
                {/* Render logo preview */}
                {logoEnabled && companyLogo && (() => {
                  // Padding as percentage of container - matches Cloudinary x/y offset behavior
                  const padding = `${Math.max(1, logoPadding / 5)}%`;
                  
                  // Position map matching Cloudinary gravity positions
                  const positionMap = {
                    north_west: { top: padding, left: padding },
                    north: { top: padding, left: '50%', transform: 'translateX(-50%)' },
                    north_east: { top: padding, right: padding },
                    west: { top: '50%', left: padding, transform: 'translateY(-50%)' },
                    center: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
                    east: { top: '50%', right: padding, transform: 'translateY(-50%)' },
                    south_west: { bottom: padding, left: padding },
                    south: { bottom: padding, left: '50%', transform: 'translateX(-50%)' },
                    south_east: { bottom: padding, right: padding },
                  };
                  
                  const posStyle = positionMap[logoPosition] || positionMap.south_east;
                  
                  return (
                    <div 
                      className="preview-logo-element" 
                      style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        top: 0,
                        left: 0,
                        pointerEvents: 'none',
                      }}
                    >
                      <img 
                        src={companyLogo.secure_url || companyLogo.url} 
                        alt="Logo" 
                        style={{ 
                          position: 'absolute',
                          width: `${logoScale}%`,
                          maxWidth: '95%',
                          height: 'auto',
                          display: 'block',
                          opacity: logoOpacity / 100,
                          ...posStyle,
                        }}
                      />
                    </div>
                  );
                })()}
              </div>
              
              {/* Clickable Format Buttons */}
              <div className="preview-format-buttons">
                {selectedSizes.map(size => {
                  const sizeInfo = OUTPUT_SIZES.find(s => s.key === size);
                  if (!sizeInfo) return null;
                  const aspectMap = {
                    'instagram_story': '9:16',
                    'instagram_portrait': '4:5',
                    'instagram_square': '1:1',
                    'original': '16:9'
                  };
                  const aspect = aspectMap[size] || '4:5';
                  return (
                    <button
                      key={size}
                      className={`format-btn ${previewAspect === aspect ? 'active' : ''}`}
                      onClick={() => setPreviewAspect(aspect)}
                    >
                      {sizeInfo.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Image Library */}
        {imageLibrary.length > 0 && (
          <div id="image-library" className="library-section">
            <div className="library-header">
              <h2 className="library-title">Image Library</h2>
              <span className="library-count">{imageLibrary.length} images</span>
            </div>
            
            {/* Library Actions */}
            <div className="library-actions">
              <div className="library-actions-left">
                <button 
                  className={`library-action-btn ${selectedLibraryIds.length === imageLibrary.length ? 'active' : ''}`}
                  onClick={selectAllLibrary}
                  data-testid="select-all-btn"
                >
                  <Check size={16} />
                  {selectedLibraryIds.length === imageLibrary.length ? 'Deselect All' : 'Select All'}
                </button>
                {selectedLibraryIds.length > 0 && (
                  <>
                    <span className="selected-count">{selectedLibraryIds.length} selected</span>
                    <button 
                      className="library-action-btn danger"
                      onClick={removeSelectedFromLibrary}
                      data-testid="remove-selected-btn"
                    >
                      <Trash2 size={16} />
                      Remove
                    </button>
                  </>
                )}
              </div>
              <div className="library-actions-right">
                <button 
                  className="library-action-btn"
                  onClick={handleStartOver}
                  data-testid="start-over-btn"
                >
                  <RefreshCw size={16} />
                  Start Over
                </button>
              </div>
            </div>

            {/* Individual Crops */}
            {cropAssets.length > 0 && (
              <div className="library-group">
                <h3 className="group-title">Individual Crops ({cropAssets.length})</h3>
                <div className="library-grid">
                  {cropAssets.map((asset) => {
                    const isSelected = selectedLibraryIds.includes(asset.id);
                    return (
                      <div 
                        key={asset.id} 
                        className={`library-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleLibrarySelection(asset.id)}
                        data-testid={`library-item-${asset.id}`}
                      >
                        <div className="library-card-select">
                          <div className={`select-checkbox ${isSelected ? 'checked' : ''}`}>
                            {isSelected && <Check size={14} />}
                          </div>
                        </div>
                        <div className="library-card-preview">
                          <img src={asset.secure_url} alt={asset.filename} loading="lazy" />
                        </div>
                        <div className="library-card-info">
                          <span className="library-card-format">{asset.format_label}</span>
                        </div>
                        <div className="library-card-actions" onClick={e => e.stopPropagation()}>
                          <button 
                            className="library-btn"
                            onClick={() => downloadImage(asset)}
                            title="Download"
                          >
                            <Download size={14} />
                          </button>
                          <button 
                            className="library-btn mobile-only"
                            onClick={() => saveToDevice(asset)}
                            title="Save to device"
                          >
                            <Smartphone size={14} />
                          </button>
                          <button 
                            className="library-btn"
                            onClick={() => copyUrl(asset.secure_url)}
                            title="Copy URL"
                          >
                            <Copy size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Collages */}
            {collageAssets.length > 0 && (
              <div className="library-group">
                <h3 className="group-title">Collages ({collageAssets.length})</h3>
                <div className="library-grid collages">
                  {collageAssets.map((asset) => {
                    const isSelected = selectedLibraryIds.includes(asset.id);
                    return (
                      <div 
                        key={asset.id} 
                        className={`library-card collage ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleLibrarySelection(asset.id)}
                      >
                        <div className="library-card-select">
                          <div className={`select-checkbox ${isSelected ? 'checked' : ''}`}>
                            {isSelected && <Check size={14} />}
                          </div>
                        </div>
                        <div className="library-card-preview">
                          <img src={asset.secure_url} alt={asset.filename} loading="lazy" />
                        </div>
                        <div className="library-card-info">
                          <span className="library-card-format">{asset.format_label}</span>
                        </div>
                        <div className="library-card-actions" onClick={e => e.stopPropagation()}>
                          <button 
                            className="library-btn"
                            onClick={() => downloadImage(asset)}
                            title="Download"
                          >
                            <Download size={14} />
                          </button>
                          <button 
                            className="library-btn mobile-only"
                            onClick={() => saveToDevice(asset)}
                            title="Save to device"
                          >
                            <Smartphone size={14} />
                          </button>
                          <button 
                            className="library-btn"
                            onClick={() => copyUrl(asset.secure_url)}
                            title="Copy URL"
                          >
                            <Copy size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Download & WhatsApp Section */}
            <div className="library-download-section">
              <div className="library-download-row">
                {selectedLibraryIds.length > 0 ? (
                  <Button 
                    onClick={downloadSelectedImages}
                    className="download-selected-btn"
                    data-testid="download-selected-btn"
                  >
                    <Download className="btn-icon" />
                    Download Selected ({selectedLibraryIds.length})
                  </Button>
                ) : (
                  <Button 
                    onClick={downloadAllImages}
                    className="download-all-btn"
                    data-testid="download-all-btn"
                  >
                    <Download className="btn-icon" />
                    Download All ({imageLibrary.length})
                  </Button>
                )}

                {/* WhatsApp Share */}
                <div className="whatsapp-share-section" data-testid="whatsapp-share">
                  {!showWhatsApp ? (
                    <Button
                      onClick={() => setShowWhatsApp(true)}
                      className="whatsapp-btn"
                      data-testid="whatsapp-toggle-btn"
                    >
                      <MessageCircle className="btn-icon" />
                      Send via WhatsApp
                    </Button>
                  ) : (
                    <div className="whatsapp-panel">
                      <select
                        className="whatsapp-agent-select"
                        value={selectedAgentPhone}
                        onChange={(e) => setSelectedAgentPhone(e.target.value)}
                        data-testid="whatsapp-agent-select"
                      >
                        <option value="">Select agent...</option>
                        {agents.filter(a => a.phone).map(a => (
                          <option key={a.id} value={a.phone}>{a.name} ({a.phone})</option>
                        ))}
                        <option value="manual">Enter number manually</option>
                      </select>
                      {selectedAgentPhone === 'manual' && (
                        <input
                          type="tel"
                          placeholder="+44..."
                          className="whatsapp-phone-input"
                          onChange={(e) => setSelectedAgentPhone(e.target.value)}
                          data-testid="whatsapp-phone-input"
                        />
                      )}
                      <div className="whatsapp-actions">
                        <Button
                          onClick={handleWhatsAppShare}
                          disabled={!selectedAgentPhone || selectedAgentPhone === 'manual'}
                          className="whatsapp-send-btn"
                          data-testid="whatsapp-send-btn"
                        >
                          <MessageCircle size={14} />
                          Send
                        </Button>
                        <button
                          className="whatsapp-cancel"
                          onClick={() => { setShowWhatsApp(false); setSelectedAgentPhone(''); }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SocialImageBuilder;
